import * as React from 'react';
import * as Edge from '../../../core';
import queryString from 'query-string';
import { Link } from 'react-router-dom';
import { RouteComponentProps } from 'react-router';
import { connect } from 'react-redux';

import { AppState } from '../../../store';
import { withLoadDataDefaultConfig } from '../../../components/loadData';
import errorLoadingWrapperHOC from '../../../components/errorLoadingWrapper/errorLoadingWrapperHOC';
import { SessionService } from '../../../services/sessionService';
import { UserService } from '../../../services/userService';
import { StatsService } from '../../../services/statsService';
import { PlanService } from '../../../services/planService';
import { TeamService } from '../../../services/teamService';
import JsonDebug from '../../../components/jsonDebug';
import ScrollToTopOnMount from '../../../components/global/scrollToTopOnMount';
import InfoBanner from '../../../components/infoBanner';
import PerformanceSummary from '../../../components/performanceSummary';
import { EvaluationResultsPdf } from './resultsPdf';
import { buildFocusAreas } from './utils';

import { ReactComponent as MedalIcon } from '../../../assets/images/icons/Medal.svg';
import { ReactComponent as MedalConfettiIcon } from '../../../assets/images/icons/MedalConfetti.svg';
import { ReactComponent as CalendarIcon } from '../../../assets/images/icons/Calendar.svg';
import { ReactComponent as DifficultyIcon } from '../../../assets/images/icons/Difficulty.svg';
import { ReactComponent as StatsIcon } from '../../../assets/images/icons/Stats.svg';
import { ReactComponent as ShieldIcon } from '../../../assets/images/icons/Shield.svg';
import { ReactComponent as InfoIcon } from '../../../assets/images/icons/Info.svg';
import { ReactComponent as HouseIcon } from '../../../assets/images/icons/House.svg';

import './index.scss';

export interface EvaluationResultsProps {
	daysTimeSpan?: number;
	isPdf?: boolean;
	position?: Edge.Models.Position;
	results: Edge.Models.ScoreCardResult;
	session: Edge.Models.BaseSessionResponse;
	trainingPlan?: Edge.Models.TrainingPlanResult;
}

const NEXT_STEPS = [
	{
		title: 'Start Your Plan',
		description: 'Begin Week 1 and complete your first training session.',
		Icon: CalendarIcon,
		iconClassName: 'evaluation_before_icon_calendar',
	},
	{
		title: 'Build Consistency',
		description: 'Complete all sessions to build momentum and skills.',
		Icon: DifficultyIcon,
	},
	{
		title: 'Unlock Next Test',
		description: 'After your plan is complete, you can take your next Edge Test.',
		Icon: ShieldIcon,
	},
	{
		title: 'Track Improvement',
		description: 'See how your scores improve and adjust your plan.',
		Icon: StatsIcon,
	},
];

const TimelineArrow: React.FunctionComponent = () => (
	<svg className="evaluation-results_timeline_arrow" viewBox="0 0 32 12">
		<path
			d="M0 6h17M14 2.5l7 3.5-7 3.5"
			fill="none"
			stroke="currentColor"
			strokeLinecap="round"
			strokeLinejoin="round"
			strokeWidth="2"
		/>
	</svg>
);

function EvaluationResults({ session, results, trainingPlan }: EvaluationResultsProps) {
	const isFirstEvaluation = results.averagePreviousEdgeScore === 0;
	const firstName = (session.user && session.user.firstName) || 'Athlete';
	const focusAreas = buildFocusAreas(trainingPlan && trainingPlan.plan.phases);
	const currentPhase =
		trainingPlan && trainingPlan.plan.phases.find((phase) => phase.id === trainingPlan.currentPlanPhaseId);
	const requiredSessions = (currentPhase && currentPhase.requiredSessions) || 3;

	return (
		<div className="evaluation evaluation-results">
			<ScrollToTopOnMount />

			<div className="evaluation-results_container">
				<div className="evaluation-results_topbar">
					<Link className="evaluation-results_back" to="/dashboard">
						<HouseIcon />
						Back to Dashboard
					</Link>
				</div>

				<div className="evaluation-results_hero">
					<div className="evaluation-results_hero_main">
						<div className="evaluation-results_badge">
							<div className="evaluation_deliverable_icon evaluation-results_badge_circle">
								<MedalIcon className="evaluation-results_badge_icon" />
							</div>
							<MedalConfettiIcon className="evaluation-results_badge_confetti" aria-hidden="true" />
						</div>
						<div>
							<h1 className="evaluation_title evaluation-results_title">Great work, {firstName}!</h1>
							<p className="evaluation_snapshot_text">
								You've completed your latest Edge Test—another step toward strengthening your visual and
								cognitive performance.
							</p>
						</div>
					</div>

					<aside className="evaluation-results_whats-next">
						<h2 className="evaluation_section_label">What&apos;s Next?</h2>
						<p>
							Your personalized training plan has been built from your Edge Test results. Start Week 1 to
							begin strengthening the visual and cognitive skills that matter most.
						</p>
						<Link className="cta_btn btn_small evaluation-results_cta" to="/training/myPlan">
							<span>View My Training Plan</span>
							<span className="cta_chevron" />
						</Link>
					</aside>
				</div>

				<PerformanceSummary isFirstEvaluation={isFirstEvaluation} session={session} />

				<InfoBanner icon={InfoIcon}>
					Rankings will update after each Edge Test so you can track your progress over time.
				</InfoBanner>

				<section className="evaluation_panel evaluation-results_section">
					<header className="evaluation-results_section_header">
						<div className="evaluation_panel_heading">
							<span className="evaluation_skill_number">2</span>
							<h2 className="evaluation_section_label">Your Next Steps</h2>
						</div>
					</header>

					<div className="evaluation-results_timeline">
						{NEXT_STEPS.map((step, index) => (
							<React.Fragment key={step.title}>
								<div className="evaluation-results_timeline_step">
									<div
										className={`evaluation_before_icon evaluation-results_timeline_icon${
											step.iconClassName ? ` ${step.iconClassName}` : ''
										}`}
									>
										<step.Icon />
									</div>
									<h4>{step.title}</h4>
									<p>{step.description}</p>
								</div>
								{index < NEXT_STEPS.length - 1 && <TimelineArrow />}
							</React.Fragment>
						))}
					</div>

					<div className="evaluation_before evaluation-results_plan-preview">
						<div className="evaluation_panel_heading evaluation-results_plan-preview_header">
							<CalendarIcon />
							<div>
								<h2 className="evaluation_section_label">Your Training Plan Preview</h2>
								<p className="evaluation_skills_hint">
									Your My Plan is personalized to strengthen your visual skills over time.
								</p>
							</div>
						</div>
						<div className="evaluation-results_plan-preview_body">
							<div className="evaluation-results_plan-preview_focus">
								{focusAreas.map((area) => (
									<div
										className={`evaluation-results_focus-item evaluation-results_focus-item_${
											area.colorClass
										}`}
										key={area.title}
									>
										<area.Icon />
										<span>{area.title}</span>
									</div>
								))}
							</div>
							<div className="evaluation-results_plan-preview_details">
								<div className="evaluation-results_plan-stats">
									<div className="evaluation-results_plan-stat">
										<span className="evaluation_section_label evaluation-results_plan-label">
											Weekly Commitment
										</span>
										<strong>{requiredSessions} Sessions</strong>
										<span>10-15 Min Each</span>
									</div>
									<div className="evaluation-results_plan-stat">
										<span className="evaluation_section_label evaluation-results_plan-label">
											First Goal
										</span>
										<strong>Complete Week 1</strong>
										<span>Build your foundation</span>
									</div>
								</div>
								<Link className="cta_btn btn_small evaluation-results_cta" to="/training/myPlan">
									<span>Start Week 1 Session</span>
									<span className="cta_chevron" />
								</Link>
							</div>
						</div>
					</div>
				</section>

				<InfoBanner icon={InfoIcon}>You can view this summary anytime from your Stats page.</InfoBanner>
			</div>

			<JsonDebug data={session} hide />
		</div>
	);
}

interface EvaluationResultsWrapperRouteProps {
	id: string;
	isProCompare?: string;
	isPdf?: string;
}

export interface EvaluationResultsWrapperProps
	extends EvaluationResultsProps,
		RouteComponentProps<EvaluationResultsWrapperRouteProps> {
	currentTeam?: Edge.Models.Team;
	proPercentiles?: Edge.Models.ProPercentile[];
}

const EvaluationResultsWrapper: React.FunctionComponent<EvaluationResultsWrapperProps> = ({
	session,
	proPercentiles,
	position,
	results,
	trainingPlan,
	currentTeam,
	isPdf,
}) => {
	return isPdf ? (
		<EvaluationResultsPdf
			session={session}
			proPercentiles={proPercentiles}
			position={position}
			results={results}
			currentTeam={currentTeam}
		/>
	) : (
		<EvaluationResults session={session} position={position} results={results} trainingPlan={trainingPlan} />
	);
};

function mapStateToProps(state: AppState) {
	const { timeSpan } = state.timeSpanState;

	return { daysTimeSpan: timeSpan.days };
}

export default connect(mapStateToProps)(
	withLoadDataDefaultConfig(
		errorLoadingWrapperHOC(EvaluationResultsWrapper),
		(props: EvaluationResultsWrapperProps) => {
			const query = queryString.parse(props.location.search);

			const isProCompareParam =
				query && (Array.isArray(query.isProCompare) ? query.isProCompare[0] : query.isProCompare);
			const isPdfParam = query && (Array.isArray(query.isPdf) ? query.isPdf[0] : query.isPdf);

			return {
				daysTimeSpan: props.daysTimeSpan!,
				id: props.match.params.id,
				isProCompare: isProCompareParam && isProCompareParam.toLowerCase() === 'true',
				isPdf: isPdfParam && isPdfParam.toLowerCase() === 'true',
			};
		},
		async (props) => {
			const session = await SessionService.getSession(props.id);
			const [data, currentTeam, trainingPlan] = await Promise.all([
				UserService.getAthleteCard(session.user!.id, props.daysTimeSpan, session.teamId),
				TeamService.getAll().then((teams) => teams.find((team) => team.id === session.teamId)),
				PlanService.getMyTrainingPlan(),
			]);
			const { user, position, latestEdgeScore, previousEdgeScore, totalSessions, totalHoursTrained } = data;
			const results: Edge.Models.ScoreCardResult = {
				averageRecentEdgeScore: latestEdgeScore ? latestEdgeScore : 0,
				averagePreviousEdgeScore: previousEdgeScore ? previousEdgeScore : 0,
				totalSessions,
				totalHoursTrained,
			};
			return {
				currentTeam,
				isPdf: !!props.isPdf,
				position,
				proPercentiles: props.isProCompare && (await StatsService.getProPercentiles(props.id)),
				results,
				session,
				trainingPlan,
				user,
			};
		}
	)
);
